/**
 * @file     ScenarioLesson.tsx
 * @readme   ../README.md
 * @purpose  Scenario lesson type -- presents real-world situations with animated choice reveals and SVG feedback icons.
 */
'use client';

import '@/core/i18n/deferred/academy';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme';
import { microLabelSx } from '@/components/hub/hubKitStyles';
import type { LessonContent } from '@/content-system/academy/types';
import { QuizSection } from '../quiz/QuizSection';
import { LegalDisclaimer } from '../LegalDisclaimer';
import { IconCorrect, IconIncorrect } from '../icons/AcademyIcons';
import { DragDropQuiz } from '../quiz/DragDropQuiz';

interface ScenarioLessonProps {
  content: LessonContent;
  onContentConsumed?: () => void;
  onQuizPassed?: () => void;
}

export function ScenarioLesson({ content, onContentConsumed, onQuizPassed }: ScenarioLessonProps) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;
  const scenarios = content.scenarios ?? [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const isLast = currentIndex === scenarios.length - 1;
  const reachedEnd = isLast && revealed;

  useEffect(() => {
    if (reachedEnd) onContentConsumed?.();
  }, [reachedEnd, onContentConsumed]);

  if (scenarios.length === 0) return null;

  const scenario = scenarios[currentIndex];
  const selectedObj = scenario.choices.find((c) => c.id === selectedChoice);

  const handleChoiceClick = (choiceId: string) => {
    if (revealed) return;
    setSelectedChoice(choiceId);
    setRevealed(true);
  };

  const handleNext = () => {
    setSelectedChoice(null);
    setRevealed(false);
    setCurrentIndex((p) => p + 1);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        '@keyframes scenarioFadeIn': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes scenarioSlideUp': {
          from: { opacity: 0, transform: 'translateY(16px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes choiceStagger': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& *': {
            animationDuration: '0ms !important',
            animationDelay: '0ms !important',
          },
        },
      }}
    >
      {/* Drag-and-drop exercises */}
      {content.dragDrop && content.dragDrop.length > 0 && content.dragDrop.map((dd, idx) => (
        <DragDropQuiz
          key={idx}
          prompt={dd.prompt}
          items={dd.items}
          correctOrder={dd.correctOrder}
          explanation={dd.explanation}
        />
      ))}

      {content.readingBody && (
        <Typography sx={{ fontSize: theme.typography.bodyLg, color: theme.semanticColors.textPrimary, lineHeight: 1.8, mb: 1 }}>
          {content.readingBody}
        </Typography>
      )}

      <Typography sx={microLabelSx}>
        {t('academy.lessonPage.scenarioCounter', { defaultValue: 'Scenario {{n}} of {{total}}', n: currentIndex + 1, total: scenarios.length })}
      </Typography>

      <Box
        sx={{
          p: 3,
          borderRadius: `${theme.layout.radius.lg}px`,
          backgroundColor: theme.semanticColors.surfaceCard,
          border: `1px solid ${theme.semanticColors.borderSubtle}`,
          // The first scenario is server-rendered and never animates in.
          animation: currentIndex > 0 ? `scenarioFadeIn ${dur.normal}ms ${ease.decelerate}` : 'none',
        }}
      >
        <Typography sx={{ fontSize: theme.typography.bodyLg, color: theme.semanticColors.textPrimary, lineHeight: 1.7, fontStyle: 'italic' }}>
          {scenario.situation}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {scenario.choices.map((choice, index) => {
          const isSelected = selectedChoice === choice.id;
          let borderColor = theme.semanticColors.borderSubtle;
          let bgColor = 'transparent';

          if (revealed) {
            if (choice.isCorrect) {
              borderColor = theme.semanticColors.statusSuccess;
              bgColor = alpha(theme.rawSemanticColors.statusSuccess, 0.06);
            } else if (isSelected && !choice.isCorrect) {
              borderColor = theme.semanticColors.statusError;
              bgColor = alpha(theme.rawSemanticColors.statusError, 0.06);
            }
          } else if (isSelected) {
            borderColor = theme.semanticColors.interactivePrimary;
          }

          return (
            <Button
              key={choice.id}
              variant="outlined"
              fullWidth
              onClick={() => handleChoiceClick(choice.id)}
              disabled={revealed && !isSelected && !choice.isCorrect}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: `${theme.layout.radius.md}px`,
                borderColor,
                backgroundColor: bgColor,
                py: 2,
                px: 2,
                minHeight: 44,
                transition: `all ${dur.fast}ms ${ease.standard}`,
                animation: currentIndex > 0 ? `choiceStagger ${dur.normal}ms ${ease.decelerate} both` : 'none',
                animationDelay: `${index * 60}ms`,
                '&:hover:not(:disabled)': {
                  borderColor: theme.semanticColors.interactivePrimary,
                  transform: 'translateX(3px)',
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.semanticColors.interactivePrimary}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Typography sx={{ fontSize: 14, color: theme.semanticColors.textPrimary }}>
                {choice.label}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {revealed && selectedObj && (
        <Box
          sx={{
            p: 2,
            borderRadius: `${theme.layout.radius.lg}px`,
            backgroundColor: selectedObj.isCorrect
              ? alpha(theme.rawSemanticColors.statusSuccess, 0.06)
              : alpha(theme.rawSemanticColors.statusError, 0.06),
            border: `1px solid ${selectedObj.isCorrect ? alpha(theme.rawSemanticColors.statusSuccess, 0.25) : alpha(theme.rawSemanticColors.statusError, 0.25)}`,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            animation: `scenarioSlideUp ${dur.normal}ms ${ease.decelerate}`,
          }}
        >
          <Box sx={{ pt: 0.2, flexShrink: 0 }}>
            {selectedObj.isCorrect ? <IconCorrect size={18} /> : <IconIncorrect size={18} />}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 0.5, color: selectedObj.isCorrect ? theme.semanticColors.statusSuccess : theme.semanticColors.statusError }}>
              {selectedObj.isCorrect ? 'Correct.' : 'Not quite.'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: theme.semanticColors.textSecondary, lineHeight: 1.6 }}>
              {selectedObj.feedback}
            </Typography>
          </Box>
        </Box>
      )}

      {revealed && !isLast && (
        <Box sx={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleNext}
            sx={{
              textTransform: 'none',
              borderRadius: `${theme.layout.radius.md}px`,
              minHeight: 44,
              transition: `all ${dur.fast}ms ${ease.standard}`,
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            Next Scenario
          </Button>
        </Box>
      )}

      {revealed && isLast && (
        <>
          {content.keyTakeaways.length > 0 && (
            <Box sx={{ p: 3, borderRadius: `${theme.layout.radius.lg}px`, backgroundColor: theme.semanticColors.surfaceCard, border: `1px solid ${theme.semanticColors.borderSubtle}` }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1, color: theme.semanticColors.textPrimary }}>Key Takeaways</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {content.keyTakeaways.map((t, i) => (
                  <Typography component="li" key={i} sx={{ fontSize: 14, color: theme.semanticColors.textSecondary, mb: 0.5 }}>
                    {t}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {content.quiz.length > 0 && <QuizSection questions={content.quiz} lessonId={content.id} onQuizPassed={onQuizPassed} />}

      {/* Legal disclaimer and sources */}
      <LegalDisclaimer sources={content.sources} lastVerified={content.lastVerified} />
        </>
      )}
    </Box>
  );
}
